// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import {
  BaseDatePicker,
  BaseDynamicSelect,
  BaseLabel,
  BaseSelect,
  BaseTable,
  Loading,
  UnderDevelopment,
} from '~/components/common';
// Others
import { IFeatureUtilizationReport, IQueryParamListExport } from '~/utils/interface/reports';
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_STRING,
  EMPTY_VALUE,
  optionsDeliveryOrPickUp,
  optionTransactionType,
} from '~/utils/constants/common';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  IBaseOption,
  IHeaderCellType,
  IPayloadCallApiFn,
  IQueryParamBase,
  IResultCallApiFn,
} from '~/utils/interface/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { exportDataReport, getListFeatureUtilizationReport } from '~/thunks/reports/reportsThunk';
import { DateFormatEnum, ReportTypeEnum, TypeCrmEnum } from '~/utils/enum/common';
import { IClient, IOptionClient, IQueryParamClient } from '~/utils/interface/client';
import { getListClient } from '~/thunks/client/clientThunk';
import { getFullName } from '~/utils/helper/common';
import { getListCategory } from '~/thunks/category/categoryThunk';
import { ICategory } from '~/utils/interface/category';
// Styles, images, icons
import styles from './FeatureUtilizationReport.module.scss';
import { icons } from '~/assets';
import moment from 'moment/moment';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IFeatureUtilizationReport>[] => {
  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_future_utilization_table_product_title')}</Box>
        </Stack>
      ),
      render: (record: IFeatureUtilizationReport) => (
        <Stack>
          <Typography className={cx('vendorName')}>
            {record?.name}
            {record?.variantName ? ` - ${record.variantName}` : ''}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 'category',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_future_utilization_table_category_title')}</Box>
        </Stack>
      ),
      render: (record: IFeatureUtilizationReport) => {
        return <Typography className={cx('textValue')}>{record?.category?.name || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'overbookedQuantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_future_utilization_table_overbook_title')}</Box>
        </Stack>
      ),
      render: (record: IFeatureUtilizationReport) => {
        return <Typography className={cx('textQuantity')}>{record?.overbookedQuantity}</Typography>;
      },
    },
    {
      id: 'inventory',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_future_utilization_table_inventory_title')}</Box>
        </Stack>
      ),
      render: (record: IFeatureUtilizationReport) => {
        return <Typography className={cx('textQuantity')}>{record?.quantity}</Typography>;
      },
    },
    {
      id: 'total',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_future_utilization_table_total_title')}</Box>
        </Stack>
      ),
      render: (record: IFeatureUtilizationReport) => {
        const total = +record.quantity + +record.overbookedQuantity;
        return <Typography className={cx('textSubtotal')}>{total || EMPTY_VALUE}</Typography>;
      },
    },
  ];
};

const FeatureUtilizationReport = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [featureList, setFeatureList] = useState<IFeatureUtilizationReport[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
  });
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListFeatureReport();
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListFeatureReport = () => {
    setIsLoading(true);

    dispatch(getListFeatureUtilizationReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses, pagination } = res.data;

        setFeatureList(responses);
        setPagination(pagination);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListFeatureReport();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.FUTURE_UTILIZATION,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamsObject({ ...paramsObject, page });
    setSearchParams({ ...params, page: String(page) });
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const fetchOptionsClient = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionClient>> => {
      const { searchKey, page } = payload;
      const payloadParams: IQueryParamClient = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        type: TypeCrmEnum.CLIENT,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListClient(payloadParams)).unwrap();

        const { responses: listClient, pagination } = resp?.data;

        const formatOptions: IOptionClient[] = listClient?.map((item: IClient) => ({
          value: item?.id,
          label:
            item?.firstName || item?.lastName
              ? getFullName({ firstName: item?.firstName, lastName: item?.lastName })
              : item?.entityName,
          email: item?.clientContacts?.[0]?.email || EMPTY_STRING,
        }));

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const fetchOptionsCategory = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;
      const payloadParams: IQueryParamBase = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListCategory(payloadParams)).unwrap();

        const { responses: listCategory, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = handleConvertOptionCategory(listCategory);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const fetchOptionTransportation = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsDeliveryOrPickUp,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionTransactionType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTransactionType,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleConvertOptionCategory = (data: ICategory[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    return options;
  };
  //#endregion Handle Function

  return (
    <Stack id='reportsSubrentalsPage' className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_future_utilization_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_future_utilization_desc')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'start'} rowSpacing={'24px'} container>
          <Grid2 size={4}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={4}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={4} />

          <Grid2 size={3}>
            <BaseDynamicSelect
              multiple={true}
              isSearch={true}
              label={t('report_table_category')}
              callApiFn={fetchOptionsCategory}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  categoryId: data?.idSelected,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseSelect
              gap={8}
              height={40}
              styleType='contained'
              label={t('reports_filter_client')}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  clientId: data?.value,
                }));
              }}
              callApiFn={fetchOptionsClient}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseSelect
              gap={8}
              height={40}
              styleType='contained'
              label={t('reports_filter_transportation')}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  deliveryOrPickUp: data?.value,
                }));
              }}
              callApiFn={fetchOptionTransportation}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseSelect
              gap={8}
              height={40}
              styleType='contained'
              label={t('reports_filter_transaction_type')}
              placeholder={t('reports_filter_all_status')}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  transactionType: data?.value,
                }));
              }}
              callApiFn={fetchOptionTransactionType}
            />
          </Grid2>
        </Grid2>

        <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Box className={cx('tableContainer')}>
        <BaseTable
          dataSource={featureList}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default FeatureUtilizationReport;
